// src/components/lesson/VideoSection.tsx
//
// "Watch & learn" — renders the curated Khan Academy videos for a
// lesson topic. Sits below the worked examples + key takeaways since
// the videos are third-party supplements, not the primary teaching.
//
// Click a thumbnail → opens the YouTube embed in a modal.

'use client'

import { useEffect, useState } from 'react'
import { PlayCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { getYouTubeId, getYouTubeThumbnail, type TopicVideo } from '@/lib/content/topic-videos'
import { videoByYouTubeId } from '@/lib/content/video-catalog'
import { setOnScreenMaterial } from '@/lib/tutor/on-screen-material'

/** Videos we made ourselves — they lead the list and change the section's framing. */
const OUR_SOURCE = 'UnlimitedTests'

export default function VideoSection({ topic, videos }: { topic: string; videos: TopicVideo[] }) {
  const [active, setActive] = useState<TopicVideo | null>(null)
  // Once the student plays one of OUR videos, Sage reads its written walkthrough
  // (material-context.ts) — "the technique from this video" then means this one.
  // It stays the video on screen after the player closes, until the page goes.
  const [watched, setWatched] = useState<{
    collection: string
    slug: string
    title: string
  } | null>(null)
  useEffect(() => {
    if (!watched) return
    setOnScreenMaterial({
      video: { collection: watched.collection, slug: watched.slug },
      label: `${topic} lesson · ${watched.title}`,
    })
  }, [watched, topic])
  useEffect(() => () => setOnScreenMaterial({ video: null }), [])
  const play = (v: TopicVideo) => {
    setActive(v)
    const ours = videoByYouTubeId(getYouTubeId(v.url))
    if (ours) setWatched({ collection: ours.collection, slug: ours.slug, title: ours.title })
  }
  if (!videos || videos.length === 0) return null
  /* The old copy named Khan Academy in prose and called every video a third-party
     supplement. That is the wrong framing for our own videos, so it is now decided by
     what is actually in the list — and ours lead. */
  const ours = videos.some((v) => v.source === OUR_SOURCE)
  const ordered = [...videos].sort(
    (a, b) => Number(b.source === OUR_SOURCE) - Number(a.source === OUR_SOURCE),
  )

  return (
    <section className="mb-8">
      <h2 className="mb-1 flex items-center gap-2 text-xl font-semibold text-foreground">
        <PlayCircle className="h-5 w-5 text-brand-ink" />
        Watch &amp; learn
      </h2>
      <p className="mb-3 text-sm text-muted-foreground">
        {ours
          ? `Short walkthroughs on ${topic} — ours, plus curated picks. Watch one if a written explanation isn't clicking, or after to reinforce.`
          : `Curated walkthroughs on ${topic}. They're complementary to this lesson — watch one if a written explanation isn't clicking, or after to reinforce.`}
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {ordered.map((v) => (
          <VideoThumb key={v.url} video={v} onPlay={play} />
        ))}
      </div>

      {active && <PlayerModal video={active} onClose={() => setActive(null)} />}
    </section>
  )
}

function VideoThumb({ video, onPlay }: { video: TopicVideo; onPlay: (v: TopicVideo) => void }) {
  const thumb = getYouTubeThumbnail(video.url)
  return (
    <button
      type="button"
      onClick={() => onPlay(video)}
      className="group relative overflow-hidden rounded-lg border border-border bg-card/40 text-left transition hover:border-brand/50 hover:bg-card/70"
    >
      <div
        className={`relative w-full overflow-hidden bg-muted ${video.vertical ? 'aspect-[9/16]' : 'aspect-video'}`}
      >
        {thumb ? (
          <img
            src={thumb}
            alt={video.title}
            className="h-full w-full object-cover transition group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            No thumbnail
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition group-hover:opacity-100">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/90 text-on-brand">
            <svg className="h-5 w-5 translate-x-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
          </div>
        </div>
        {video.duration && (
          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {video.duration}
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="line-clamp-2 text-sm font-medium text-foreground">{video.title}</p>
        <div className="mt-1.5 flex items-center gap-2">
          <Badge variant="outline" className="border-slate-600 text-[10px] text-muted-foreground">
            {video.source}
          </Badge>
        </div>
      </div>
    </button>
  )
}

function PlayerModal({ video, onClose }: { video: TopicVideo; onClose: () => void }) {
  const id = getYouTubeId(video.url)
  if (!id) return null
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className={`w-full space-y-3 ${video.vertical ? 'max-w-sm' : 'max-w-3xl'}`}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-white">{video.title}</p>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-white"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
        <div
          className="relative w-full overflow-hidden rounded-lg"
          /* 16:9 is 56.25%; a 9:16 Short needs 177.78% or it renders as a sliver between bars */
          style={{ paddingBottom: video.vertical ? '177.78%' : '56.25%' }}
        >
          <iframe
            className="absolute inset-0 h-full w-full rounded-lg"
            src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="border-slate-600 text-xs text-muted-foreground">
            {video.source}
          </Badge>
          <span className="text-xs text-muted-foreground">{video.duration}</span>
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-xs text-brand-ink hover:text-brand-ink hover:underline"
          >
            Open in YouTube
          </a>
        </div>
      </div>
    </div>
  )
}
